import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaChevronDown,
  FaChevronUp,
  FaPrint,
  FaFileExcel,
  FaSearch,
  FaList,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/masterReport";
const PRINT_ROUTE = "/Asset/AssetMasterReport_Print";

// ---------- helpers ----------
const pad2 = (n) => String(n).padStart(2, "0");
const todayDMY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// dd/MM/yyyy
const parseDMY = (s) => {
  const v = String(s || "").trim();
  if (!v) return null;
  const parts = v.split("/");
  if (parts.length !== 3) return null;

  const dd = Number(parts[0]);
  const mm = Number(parts[1]);
  const yyyy = Number(parts[2]);

  if (!dd || !mm || !yyyy) return null;

  const dt = new Date(yyyy, mm - 1, dd);
  if (
    dt.getFullYear() !== yyyy ||
    dt.getMonth() !== mm - 1 ||
    dt.getDate() !== dd
  ) {
    return null;
  }

  return dt;
};

const isFutureDateDMY = (s) => {
  const d = parseDMY(s);
  if (!d) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d.getTime() > now.getTime();
};

const cmpDMY = (a, b) => {
  const da = parseDMY(a);
  const db = parseDMY(b);
  if (!da || !db) return 0;
  da.setHours(0, 0, 0, 0);
  db.setHours(0, 0, 0, 0);
  return da.getTime() - db.getTime();
};

// ✅ normal backend format:
// [{ id: 1, name: "Tipper" }]
// => dropdown label=name, value=id
const normalizeMaster = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  return list
    .filter((x) => x && (x.id !== undefined || x.name !== undefined))
    .map((x) => ({
      label: String(x.id ?? ""),
      value: String(x.value  ?? ""),
    }));
};

// land location list where id/name are strings already
const normalizeLandLoc = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  return list
    .filter((x) => x && x.id !== undefined)
    .map((x) => ({
      label: String(x.name ?? x.id),
      value: String(x.id),
    }));
};

// hypoOptions safe normalization
const normalizeHypo = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  const out = [];

  for (const x of list) {
    if (!x) continue;

    if (Array.isArray(x.id) && Array.isArray(x.name)) {
      for (let i = 0; i < Math.max(x.id.length, x.name.length); i++) {
        const id = String(x.id[i] ?? "").trim();
        const name = String(x.name[i] ?? "").trim();
        if (!id && !name) continue;
        out.push({
          label: name || id,
          value: id || name,
        });
      }
      continue;
    }

    const id = String(x.id ?? "").trim();
    const name = String(x.name ?? "").trim();
    if (!id && !name) continue;

    out.push({
      label: name || id,
      value: id || name,
    });
  }

  return out;
};

export default function AssetMasterReport() {
  const navigate = useNavigate();
  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);
  const [mode, setMode] = useState("Movable"); // Movable | Land | LandSales

  const [loadingInit, setLoadingInit] = useState(false);
  const [loading, setLoading] = useState(false);

  const [masters, setMasters] = useState({
    assetTypes: [],
    assetSubTypes: [],
    makes: [],
    models: [],
    vehicleNames: [],
    locations: [],
    hypoOptions: [],
    assetClasses: [],
    landLocations: [],
    landSalesLocations: [],
  });

  const [movableRows, setMovableRows] = useState([]);
  const [landRows, setLandRows] = useState([]);
  const [movableMeta, setMovableMeta] = useState(null);

  const [mov, setMov] = useState({
    fromDate: "",
    toDate: "",
    assetTypeId: "Select",
    assetSubTypeId: "Select",
    makeId: "Select",
    vehicleNameId: "Select",
    modelId: "Select",
    idleOnly: false,
    hypo: "Select",
    locationId: "Select",
    calibratedDate: "",
    nextCalibrationDate: "",
  });

  const [land, setLand] = useState({
    yearFrom: "",
    yearTo: "",
    pattaNo: "",
    assetCode: "",
    surveyNo: "",
    assetClassId: "Select",
    locationId: "Select",
  });

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ---------------- init ----------------
  const loadInit = async () => {
    setLoadingInit(true);
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        headers: commonHeaders,
        params: {
          finYearId,
          finYear,
          branchId,
          branchtype: branchType,
          branchType,
          userId,
          loginuserid: userId,
        },
      });

      const d = res.data?.data || {};
      setMasters({
        assetTypes: Array.isArray(d.assetTypes) ? d.assetTypes : [],
        assetSubTypes: Array.isArray(d.assetSubTypes) ? d.assetSubTypes : [],
        makes: Array.isArray(d.makes) ? d.makes : [],
        models: Array.isArray(d.models) ? d.models : [],
        vehicleNames: Array.isArray(d.vehicleNames) ? d.vehicleNames : [],
        locations: Array.isArray(d.locations) ? d.locations : [],
        hypoOptions: Array.isArray(d.hypoOptions) ? d.hypoOptions : [],
        assetClasses: Array.isArray(d.assetClasses) ? d.assetClasses : [],
        landLocations: Array.isArray(d.landLocations) ? d.landLocations : [],
        landSalesLocations: Array.isArray(d.landSalesLocations)
          ? d.landSalesLocations
          : [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setLoadingInit(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMovableRows([]);
    setLandRows([]);
    setMovableMeta(null);
  }, [mode]);

  // ---------------- dependent dropdown (assetType -> subtypes + vehicles) ----------------
  const loadSubTypesAndVehicles = async (assetTypeNumericId) => {
    try {
      const res = await axios.get(`${API_BASE}/subtypes`, {
        headers: commonHeaders,
        params: {
          assetTypeId: assetTypeNumericId,
          finYearId,
          finYear,
          branchId,
          branchtype: branchType,
          branchType,
          userId,
          loginuserid: userId,
        },
      });

      const d = res.data?.data || {};
      setMasters((p) => ({
        ...p,
        assetSubTypes: Array.isArray(d.assetSubTypes) ? d.assetSubTypes : [],
        vehicleNames: Array.isArray(d.vehicleNames)
          ? d.vehicleNames
          : p.vehicleNames,
      }));
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e.message || "Failed to load subtypes"
      );
    }
  };

  // ---------------- validations ----------------
  const validateMovableDates = () => {
    if (mov.fromDate) {
      if (!parseDMY(mov.fromDate)) {
        toast.error("From Date must be dd/MM/yyyy");
        return false;
      }
      if (isFutureDateDMY(mov.fromDate)) {
        toast.error("Select from date lesser than current date");
        return false;
      }
    }

    if (mov.toDate) {
      if (!parseDMY(mov.toDate)) {
        toast.error("To Date must be dd/MM/yyyy");
        return false;
      }
      if (isFutureDateDMY(mov.toDate)) {
        toast.error("Select to date lesser than current date");
        return false;
      }
    }

    if (mov.fromDate && mov.toDate) {
      if (cmpDMY(mov.toDate, mov.fromDate) < 0) {
        toast.error("Please select from date less than to date");
        return false;
      }
    }

    if (mov.calibratedDate && isFutureDateDMY(mov.calibratedDate)) {
      toast.error("Calibrated Date cannot be future");
      return false;
    }

    if (mov.nextCalibrationDate && isFutureDateDMY(mov.nextCalibrationDate)) {
      toast.error("Next Calibration Date cannot be future");
      return false;
    }

    return true;
  };

  const validateLandYears = () => {
    const yf = String(land.yearFrom || "").trim();
    const yt = String(land.yearTo || "").trim();
    const is4 = (s) => /^\d{4}$/.test(s);

    if (yf && !is4(yf)) {
      toast.error("Year From must be 4 digits");
      return false;
    }

    if (yt && !is4(yt)) {
      toast.error("Year To must be 4 digits");
      return false;
    }

    if (yf && yt && Number(yt) < Number(yf)) {
      toast.error("Year To must be >= Year From");
      return false;
    }

    return true;
  };

  // ---------------- default dates ----------------
  const ensureDefaultMovableDates = async () => {
    let fromDate = mov.fromDate;
    let toDate = mov.toDate;

    if (!fromDate || !toDate) {
      const res = await axios.get(`${API_BASE}/defaults/dates`, {
        headers: commonHeaders,
        params: {
          location: "%",
          finYearId,
          finYear,
          branchId,
          branchtype: branchType,
          branchType,
          userId,
          loginuserid: userId,
        },
      });

      const d = res.data?.data || {};
      if (!fromDate) fromDate = d.fromDate || "";
      if (!toDate) toDate = d.toDate || "";
      setMov((p) => ({ ...p, fromDate, toDate }));
    }

    return { fromDate, toDate };
  };

  // ---------------- payload builders ----------------
  const buildMovablePayload = () => ({
    assetTypeId: mov.assetTypeId,
    assetSubTypeId: mov.assetSubTypeId,
    vehicleNameId: mov.vehicleNameId,
    fromDate: mov.fromDate,
    toDate: mov.toDate,
    makeId: mov.makeId,
    modelId: mov.modelId,
    locationId: mov.locationId,
    idleOnly: !!mov.idleOnly,
    hypo: mov.hypo,
    calibratedDate: mov.calibratedDate,
    nextCalibrationDate: mov.nextCalibrationDate,
    finYearId,
    finYear,
    branchId,
    branchtype: branchType,
    branchType,
    userId,
    loginuserid: userId,
  });

  const buildLandPayload = () => ({
    yearFrom: land.yearFrom,
    yearTo: land.yearTo,
    pattaNo: land.pattaNo,
    surveyNo: land.surveyNo,
    assetCode: land.assetCode,
    assetClassId: land.assetClassId,
    locationId: land.locationId,
    finYearId,
    finYear,
    branchId,
    branchtype: branchType,
    branchType,
    userId,
    loginuserid: userId,
  });

  // ---------------- actions ----------------
  const onViewAbstract = async () => {
    setLoading(true);
    try {
      if (mode === "Movable") {
        if (!validateMovableDates()) return;
        await ensureDefaultMovableDates();

        const res = await axios.post(
          `${API_BASE}/movable/abstract`,
          buildMovablePayload(),
          { headers: commonHeaders }
        );

        if (!res.data?.success) {
          throw new Error(res.data?.message || "Movable failed");
        }

        setMovableRows(res.data?.data || []);
        setMovableMeta(res.data?.meta || null);
        setLandRows([]);
      } else if (mode === "Land") {
        if (!validateLandYears()) return;

        const res = await axios.post(`${API_BASE}/land/list`, buildLandPayload(), {
          headers: commonHeaders,
        });

        if (!res.data?.success) {
          throw new Error(res.data?.message || "Land failed");
        }

        setLandRows(res.data?.data || []);
        setMovableRows([]);
        setMovableMeta(null);
      } else {
        if (!validateLandYears()) return;

        const res = await axios.post(
          `${API_BASE}/landSales/list`,
          buildLandPayload(),
          { headers: commonHeaders }
        );

        if (!res.data?.success) {
          throw new Error(res.data?.message || "Land Sales failed");
        }

        setLandRows(res.data?.data || []);
        setMovableRows([]);
        setMovableMeta(null);
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e.message || "Failed to load report"
      );
    } finally {
      setLoading(false);
    }
  };

  const onViewAll = async () => {
    return onViewAbstract();
  };

  // ---------------- PRINT FLOW ----------------
  const buildPrintQuery = (printId) => {
    const sp = new URLSearchParams();
    sp.set("id", String(printId));
    sp.set("mode", mode);

    sp.set("finYearId", finYearId || "");
    sp.set("finYear", finYear || "");
    sp.set("branchId", branchId || "");
    sp.set("branchType", branchType || "");
    sp.set("userId", userId || "");

    if (mode === "Movable") {
      sp.set("assetTypeId", mov.assetTypeId || "Select");
      sp.set("assetSubTypeId", mov.assetSubTypeId || "Select");
      sp.set("vehicleNameId", mov.vehicleNameId || "Select");
      sp.set("makeId", mov.makeId || "Select");
      sp.set("modelId", mov.modelId || "Select");
      sp.set("locationId", mov.locationId || "Select");
      sp.set("fromDate", mov.fromDate || "");
      sp.set("toDate", mov.toDate || "");
      sp.set("idleOnly", mov.idleOnly ? "true" : "false");
      sp.set("hypo", mov.hypo || "Select");
      sp.set("calibratedDate", mov.calibratedDate || "");
      sp.set("nextCalibrationDate", mov.nextCalibrationDate || "");
    } else {
      sp.set("yearFrom", land.yearFrom || "");
      sp.set("yearTo", land.yearTo || "");
      sp.set("pattaNo", land.pattaNo || "");
      sp.set("assetCode", land.assetCode || "");
      sp.set("surveyNo", land.surveyNo || "");
      sp.set("assetClassId", land.assetClassId || "Select");
      sp.set("landLocationId", land.locationId || "Select");
    }

    return sp.toString();
  };

  const onPrintView = async () => {
    try {
      if (mode === "Movable") {
        if (!validateMovableDates()) return;
        await ensureDefaultMovableDates();
      } else {
        if (!validateLandYears()) return;
      }

      navigate(`${PRINT_ROUTE}?${buildPrintQuery(0)}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Print open failed");
    }
  };

  const onPrintAll = async () => {
    try {
      if (mode === "Movable") {
        if (!validateMovableDates()) return;
        await ensureDefaultMovableDates();
      } else {
        if (!validateLandYears()) return;
      }

      navigate(`${PRINT_ROUTE}?${buildPrintQuery(1)}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Print open failed");
    }
  };

  // totals
  const landTotalPurchase = useMemo(
    () => landRows.reduce((s, r) => s + toNum(r.fld_purchasevalue), 0),
    [landRows]
  );

  // options
  const assetTypeOpts = useMemo(
    () => normalizeMaster(masters.assetTypes),
    [masters.assetTypes]
  );
  const subTypeOpts = useMemo(
    () => normalizeMaster(masters.assetSubTypes),
    [masters.assetSubTypes]
  );
  const makeOpts = useMemo(
    () => normalizeMaster(masters.makes),
    [masters.makes]
  );
  const modelOpts = useMemo(
    () => normalizeMaster(masters.models),
    [masters.models]
  );
  const vehicleOpts = useMemo(
    () => normalizeMaster(masters.vehicleNames),
    [masters.vehicleNames]
  );
  const locationOpts = useMemo(
    () => normalizeMaster(masters.locations),
    [masters.locations]
  );
  const hypoOpts = useMemo(
    () => normalizeHypo(masters.hypoOptions),
    [masters.hypoOptions]
  );
  const assetClassOpts = useMemo(
    () => normalizeMaster(masters.assetClasses),
    [masters.assetClasses]
  );
  const landLocOpts = useMemo(
    () => normalizeLandLoc(masters.landLocations),
    [masters.landLocations]
  );
  const landSalesLocOpts = useMemo(
    () => normalizeLandLoc(masters.landSalesLocations),
    [masters.landSalesLocations]
  );

  // styles
  const inputBase =
    "w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-gray-200";
  const labelCls = "text-sm font-semibold text-gray-700";
  const rowCls = "grid grid-cols-1 md:grid-cols-12 gap-3 items-center";
  const col2 = "md:col-span-2";
  const col3 = "md:col-span-3";

  const title =
    mode === "Movable"
      ? "Asset Abstract Report - Movable"
      : mode === "Land"
      ? "Asset Abstract Report - IMMovable"
      : "Asset Abstract Report - Land Sales";

  return (
    <AssetLayoutWrapper>
      <HeaderBar />
      <ToastContainer />

      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xl font-bold uppercase">{title}</div>
            <div className="text-xs text-gray-500 mt-1">INFRA SCREEN : D-13</div>
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((s) => !s)}
            className={`${buttonClass} ${hoverClass} text-white px-3 py-2 rounded-lg flex items-center gap-2`}
          >
            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            Filters
          </button>
        </div>

        <div className="mt-4 p-3 rounded-xl border bg-white">
          <div className={rowCls}>
            <div className={`${col2} ${labelCls}`}>Asset Type</div>
            <div className="md:col-span-10 flex flex-wrap gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  checked={mode === "Movable"}
                  onChange={() => setMode("Movable")}
                />
                <span>Movable</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  checked={mode === "Land"}
                  onChange={() => setMode("Land")}
                />
                <span>IMMovable</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  checked={mode === "LandSales"}
                  onChange={() => setMode("LandSales")}
                />
                <span>Land Sales</span>
              </label>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 p-4 rounded-xl border bg-white">
            {loadingInit && (
              <div className="text-sm text-gray-500">Loading masters...</div>
            )}

            {mode === "Movable" ? (
              <>
                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>From Date</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      placeholder={`dd/MM/yyyy (e.g. ${todayDMY()})`}
                      value={mov.fromDate}
                      onChange={(e) =>
                        setMov((p) => ({ ...p, fromDate: e.target.value }))
                      }
                    />
                  </div>
                  <div className="md:col-span-2" />
                  <div className={`${col2} ${labelCls}`}>To Date</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      placeholder={`dd/MM/yyyy (e.g. ${todayDMY()})`}
                      value={mov.toDate}
                      onChange={(e) =>
                        setMov((p) => ({ ...p, toDate: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>AssetType</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.assetTypeId}
                      onChange={(e) => {
                        const v = e.target.value;
                        setMov((p) => ({
                          ...p,
                          assetTypeId: v,
                          assetSubTypeId: "Select",
                          vehicleNameId: "Select",
                        }));

                        if (v && v !== "Select") {
                          loadSubTypesAndVehicles(v);
                        } else {
                          setMasters((p) => ({
                            ...p,
                            assetSubTypes: [],
                          }));
                        }
                      }}
                    >
                      <option value="Select">Select</option>
                      {assetTypeOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>AssetSubType</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.assetSubTypeId}
                      onChange={(e) =>
                        setMov((p) => ({
                          ...p,
                          assetSubTypeId: e.target.value,
                        }))
                      }
                    >
                      <option value="Select">Select</option>
                      {subTypeOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Make</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.makeId}
                      onChange={(e) =>
                        setMov((p) => ({ ...p, makeId: e.target.value }))
                      }
                    >
                      <option value="Select">Select</option>
                      {makeOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>Vehicle Name</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.vehicleNameId}
                      onChange={(e) =>
                        setMov((p) => ({
                          ...p,
                          vehicleNameId: e.target.value,
                        }))
                      }
                    >
                      <option value="Select">Select</option>
                      {vehicleOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Model</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.modelId}
                      onChange={(e) =>
                        setMov((p) => ({ ...p, modelId: e.target.value }))
                      }
                    >
                      <option value="Select">Select</option>
                      {modelOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>Options</div>
                  <div className={col3}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={mov.idleOnly}
                        onChange={(e) =>
                          setMov((p) => ({
                            ...p,
                            idleOnly: e.target.checked,
                          }))
                        }
                      />
                      <span>Idle</span>
                    </label>
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Hypothecated</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.hypo}
                      onChange={(e) =>
                        setMov((p) => ({ ...p, hypo: e.target.value }))
                      }
                    >
                      <option value="Select">Select</option>
                      {hypoOpts.map((o, idx) => (
                        <option key={idx} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>Location</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={mov.locationId}
                      onChange={(e) =>
                        setMov((p) => ({
                          ...p,
                          locationId: e.target.value,
                        }))
                      }
                    >
                      <option value="Select">Select</option>
                      {locationOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Calibrated Date</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      placeholder="dd/MM/yyyy"
                      value={mov.calibratedDate}
                      onChange={(e) =>
                        setMov((p) => ({
                          ...p,
                          calibratedDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>
                    Next Calibration Date
                  </div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      placeholder="dd/MM/yyyy"
                      value={mov.nextCalibrationDate}
                      onChange={(e) =>
                        setMov((p) => ({
                          ...p,
                          nextCalibrationDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Year From</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      maxLength={4}
                      inputMode="numeric"
                      value={land.yearFrom}
                      onChange={(e) =>
                        setLand((p) => ({
                          ...p,
                          yearFrom: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>Year To</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      maxLength={4}
                      inputMode="numeric"
                      value={land.yearTo}
                      onChange={(e) =>
                        setLand((p) => ({
                          ...p,
                          yearTo: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Patta No</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      value={land.pattaNo}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, pattaNo: e.target.value }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>Asset Code</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      value={land.assetCode}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, assetCode: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Survey No</div>
                  <div className={col3}>
                    <input
                      className={inputBase}
                      value={land.surveyNo}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, surveyNo: e.target.value }))
                      }
                    />
                  </div>

                  <div className="md:col-span-2" />

                  <div className={`${col2} ${labelCls}`}>Class of Asset</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={land.assetClassId}
                      onChange={(e) =>
                        setLand((p) => ({
                          ...p,
                          assetClassId: e.target.value,
                        }))
                      }
                    >
                      <option value="Select">Select</option>
                      {assetClassOpts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="h-4" />

                <div className={rowCls}>
                  <div className={`${col2} ${labelCls}`}>Location</div>
                  <div className={col3}>
                    <select
                      className={inputBase}
                      value={land.locationId}
                      onChange={(e) =>
                        setLand((p) => ({
                          ...p,
                          locationId: e.target.value,
                        }))
                      }
                    >
                      <option value="Select">Select</option>
                      {(mode === "Land" ? landLocOpts : landSalesLocOpts).map(
                        (o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div className="md:col-span-9" />
                </div>
              </>
            )}

            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              <button
                type="button"
                disabled={loading}
                onClick={onViewAbstract}
                className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60`}
              >
                <FaSearch />
                View (Abstract)
              </button>

              <button
                type="button"
                disabled={loading}
                onClick={onViewAll}
                className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60`}
              >
                <FaList />
                View All
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() =>
              toast.info("Excel export not added yet (need export endpoint)")
            }
            className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 flex items-center gap-2"
          >
            <FaFileExcel /> Excel
          </button>

          <button
            type="button"
            onClick={onPrintView}
            className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 flex items-center gap-2"
          >
            <FaPrint /> Print (View)
          </button>

          <button
            type="button"
            onClick={onPrintAll}
            className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 flex items-center gap-2"
          >
            <FaPrint /> Print (View All)
          </button>
        </div>

        <div className="mt-4 p-4 rounded-xl border bg-white">
          {loading && <div className="text-sm text-gray-500">Loading...</div>}

          {mode === "Movable" && (
            <>
              {movableMeta?.totals && (
                <div className="mb-3 text-sm text-gray-700">
                  <b>Totals:</b> Purchase {money(movableMeta.totals.purchaseValue)} |
                  Depreciation {money(movableMeta.totals.depreciation)} | WDV{" "}
                  {money(movableMeta.totals.wdv)}
                </div>
              )}

              <div className="overflow-auto">
                <table className="min-w-[900px] w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-sm">
                      <th className="border p-2 text-left">S.No</th>
                      <th className="border p-2 text-left">Subtype No</th>
                      <th className="border p-2 text-left">Description</th>
                      <th className="border p-2 text-right">Quantity</th>
                      <th className="border p-2 text-right">Purchase Value</th>
                      <th className="border p-2 text-right">Depreciation</th>
                      <th className="border p-2 text-right">Wdv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movableRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="border p-3 text-center text-gray-500">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      movableRows.map((r, idx) => (
                        <tr key={idx} className="text-sm">
                          <td className="border p-2">
                            {r.fld_AssetIndex ?? idx + 1}
                          </td>
                          <td className="border p-2">
                            {r.fld_subtypeNo ?? ""}
                          </td>
                          <td className="border p-2">
                            {r.AssetType ?? r.fld_assettypename ?? ""}
                          </td>
                          <td className="border p-2 text-right">
                            {toNum(r.Qty).toLocaleString("en-IN")}
                          </td>
                          <td className="border p-2 text-right">
                            {money(r.fld_purchasevalue)}
                          </td>
                          <td className="border p-2 text-right">
                            {money(r.fld_depreciationvalue)}
                          </td>
                          <td className="border p-2 text-right">
                            {money(r.fld_wdv)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {mode !== "Movable" && (
            <>
              <div className="overflow-auto">
                <table className="min-w-[1000px] w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-sm">
                      <th className="border p-2 text-left">S.No</th>
                      <th className="border p-2 text-left">Asset Code</th>
                      <th className="border p-2 text-left">Patta No</th>
                      <th className="border p-2 text-left">Survey No</th>
                      <th className="border p-2 text-center">Extent</th>
                      <th className="border p-2 text-center">Acquisition</th>
                      <th className="border p-2 text-right">Purchase Value</th>
                      <th className="border p-2 text-left">Seller Name</th>
                      <th className="border p-2 text-left">Present Holder</th>
                      <th className="border p-2 text-left">Original Document</th>
                    </tr>
                  </thead>
                  <tbody>
                    {landRows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="border p-3 text-center text-gray-500">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      landRows.map((r, idx) => (
                        <tr key={idx} className="text-sm">
                          <td className="border p-2">{idx + 1}</td>
                          <td className="border p-2">{r.fld_assetcode ?? ""}</td>
                          <td className="border p-2">{r.fld_pattano ?? ""}</td>
                          <td className="border p-2">{r.fld_surveyno ?? ""}</td>
                          <td className="border p-2 text-center">
                            {r.fld_extend ?? ""}
                          </td>
                          <td className="border p-2 text-center">
                            {r.fld_acquisitiondate ?? ""}
                          </td>
                          <td className="border p-2 text-right">
                            {money(r.fld_purchasevalue)}
                          </td>
                          <td className="border p-2">{r.fld_sellername ?? ""}</td>
                          <td className="border p-2">
                            {r.fld_propertyname ?? ""}
                          </td>
                          <td className="border p-2">
                            {r.fld_originaldoc ?? ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex justify-end gap-2 text-sm font-semibold">
                <div>Total Purchase Value:</div>
                <div>{money(landTotalPurchase)}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </AssetLayoutWrapper>
  );
}